import { PageContainer } from './Home.style';
import { useAppDispatch } from '../../core/hooks/hooks';
import HomeSection from './HomeSection/HomeSection';
import LiveActivity from './SecionsComponents/LiveActivities/LiveActivity';
import WeeklyStatistics from './SecionsComponents/WeeklyStatistics/WeeklyStatistics';
import { useEffect, useMemo, useState } from 'react';

import { Statistics } from './interface/Statistics.interface';

import gameImg from '../../assets/images/home/weekly-statistics/games.svg';
import cupImg from '../../assets/images/home/weekly-statistics/cup.svg';
import teamsJoined from '../../assets/images/home/weekly-statistics/teams-joined.svg';
import averageRank from '../../assets/images/home/weekly-statistics/average-ranl.svg';

import MasterTournamentTable from './SecionsComponents/MasterTournamentTable/MasterTournamentTable';
import MyTeams from './SecionsComponents/MyTeams/MyTeams';
import { useQuery } from 'react-query';
import { getActivities, getMasterStanding, getMyTeams, getRuiningActivities, getStatistics } from './api/home.api';
import TeamStatistics from './SecionsComponents/TeamStatistics/TeamStatistics';
import TeamCalendar from './SecionsComponents/TeamCalendar/TeamCalendar';
import RunningActivities from './SecionsComponents/RunningActivities/RunningActivities';
import { IRunningActivities, MyTeamsInterface, RunningActivitiesResponse } from './interface/RunningActivities.interface';
import { useTranslation } from 'react-i18next';
import { useMount } from 'react-use';

const HomePage = () => {
	const { t } = useTranslation();

	// *MY STATISTICS
	const statistics = useMemo<Statistics[]>(() => {
		return [
			{
				label: t('Total Activities Played'),
				value: 0,
				img: gameImg,
				des: 'homeActivitiesWithCount',
				color: '#01ABC1',
				key: 'activites_played',
			},
			{
				label: t('Joined Tournaments'),
				value: 0,
				img: cupImg,
				des: 'homeTournamentsWithCount',
				color: '#65A743',
				key: 'my_tournament_active_count',
			},
			{
				label: t('Joined Teams'),
				value: 0,
				img: teamsJoined,
				des: 'homeTeamsWithCount',
				color: '#0D4B4C',
				key: 'my_teams_count',
			},
			{
				label: t('Average Rank'),
				value: 0,
				img: averageRank,
				des: 'rank',
				color: '#76c8ce',
				key: 'average_rank',
			},
		];
	}, []);

	// * MY STATISTICS
	const { data: stats, isLoading: statsLoading } = useQuery({
		queryKey: ['statistics-loading'],
		queryFn: async (): Promise<any> => await getStatistics(),
		select: (data): any => {
			const mappedData = statistics.map((el) => {
				if (Object.keys(data).includes(el.key)) {
					if (el.key === 'average_rank') {
						return { ...el, value: data[`${el.key}`], description: t(el.des, { count: data[`${el.key}`], ordinal: true }) };
					} else {
						return { ...el, value: data[`${el.key}`], description: t(el.des, { count: data[`${el.key}`] }) };
					}
				}
			});
			return mappedData;
		},
		// keepPreviousData: true,
	});

	// *LIVE ACTIVITY CALL
	const [liveActivityNo, setLiveActivityNo] = useState<number>(1);
	const {
		data: liveActivityPaginated,
		isLoading: liveActivitiesLoading,
		refetch: refetchLiveActivities,
	} = useQuery({
		queryKey: [`live-activity-${liveActivityNo}`, liveActivityNo],
		queryFn: async (): Promise<any> => await getActivities(liveActivityNo),
		select: (data) => {
			return data;
		},
		// keepPreviousData: true,
	});

	// * TOURNAMENT STANDINGS
	const [standingNo, setStandingNo] = useState<number>(1);
	const { data: masterStanding, isLoading: masterStandingLoading } = useQuery({
		queryKey: [`masterStanding-${standingNo}`, standingNo],
		queryFn: async () => await getMasterStanding(standingNo!),
		select: (data) => {
			const mappedData = data?.records[0]?.standings?.length
				? data?.records[0]?.standings.map((res) => {
						return {
							id: res.id,
							rank: res.rank,
							status: res.status,
							isMyTeam: res.is_my_team,
							teams: res.team_name,
							rounds: res.played,
							lastScore: res.round_score,
							points: res.total_score,
						};
				  })
				: [];

			return { data: mappedData, nextPage: data.next_page_url, prevPage: data.prev_page_url, tournament: data.records[0]?.tournament_name,status: data.records[0]?.tournament_status
			};
		},
	});

	// * MY TEAMS
	const { data: myTeams, isLoading: teamsLoading } = useQuery({
		queryKey: ['teamsLoading'],
		queryFn: async (): Promise<MyTeamsInterface[]> => await getMyTeams(),

		select: (data): MyTeamsInterface[] => {
			return data;
		},
		// keepPreviousData: true,
	});

	// *RUNNING ACTIVITY CALLs
	const {
		data: runningActivities,
		isLoading: runningActivitiesLoading,
		refetch: refetchRunning,
	} = useQuery({
		queryKey: ['runningActivity'],
		queryFn: async (): Promise<RunningActivitiesResponse[]> => await getRuiningActivities(),

		select: (data): IRunningActivities[] => {
			const mappedData: IRunningActivities[] = data.map((res) => {
				return {
					name: res.name,
					type: res.type,
					slug: `/rounds/my-activity/${res.type}/${res.slug}?type=tournament`,
					image: `${process.env.REACT_APP_STORAGE}${res.image}`,
					description: res.short_description,
					targetTime: new Date(res.end_date).getTime(),
				};
			});
			return mappedData;
		},
		// keepPreviousData: true,
	});

	return (
		<PageContainer className="m-auto flex w-[90%] flex-row flex-wrap gap-8 py-[1.75em]">
			<div className="flex w-full gap-x-4 xl:gap-x-10">
				<HomeSection
					title={t('Statistics')!}
					className="w-[calc(63%_-_.5rem)] xl:w-[calc(65%_-_1.25rem)]"
					children={<WeeklyStatistics statistics={stats} loading={statsLoading} />}
				/>
				<HomeSection
					className="w-[calc(37%_-_.5rem)] xl:w-[calc(35%_-_1.25rem)]"
					title={t('liveActivities')!}
					nav_type="carousel"
					nextDisabled={!liveActivityPaginated?.links.next}
					prevDisabled={!liveActivityPaginated?.links.prev}
					nextHandle={() => setLiveActivityNo(liveActivityPaginated?.links?.next ? liveActivityNo + 1 : 1)}
					prevHandle={() => setLiveActivityNo(liveActivityPaginated?.links?.prev ? liveActivityNo - 1 : 1)}
					children={
						<LiveActivity
							liveActivity={liveActivityPaginated?.data[0]}
							liveActivityNo={liveActivityNo}
							loading={liveActivitiesLoading}
							refetch={refetchLiveActivities}
						/>
					}
				/>
			</div>
			<div className="flex w-full gap-x-4 xl:gap-x-10">
				<div className="flex w-[calc(65%_-_.5rem)] flex-row flex-wrap items-start justify-center gap-y-8 gap-x-4 xl:w-[calc(65%_-_1.25rem)] xl:gap-x-10">
					<HomeSection
						className="w-[calc(65%_-_.5rem)] xl:w-[calc(65%_-_1.25rem)]"
						title={t('tournamentStandings')!}
						nav_type="view-all"
						viewAllLink="tournament"
						children={
							<>
								<MasterTournamentTable
									data={masterStanding?.data!}
									loading={masterStandingLoading}
									next={masterStanding?.nextPage!}
									prev={masterStanding?.prevPage!}
									setStandingNo={setStandingNo}
									standingNo={standingNo}
									tournament={masterStanding?.tournament}
									status={masterStanding?.status}

								/>
							</>
						}
					/>
					<HomeSection
						className="w-[calc(35%_-_.5rem)] xl:w-[calc(35%_-_1.25rem)]"
						title={t('myTeams')!}
						children={<MyTeams teams={myTeams} teamsLoading={teamsLoading} />}
					/>
					<HomeSection className="w-[calc(65%_-_.5rem)] xl:w-[calc(65%_-_1.25rem)]" children={<TeamStatistics />} />
					<HomeSection className="w-[calc(35%_-_.5rem)] xl:w-[calc(35%_-_1.25rem)]" children={<TeamCalendar />} />
				</div>
				<div className="w-[calc(35%_-_.5rem)] xl:w-[calc(35%_-_1.25rem)]">
					<HomeSection
						className="w-full"
						title={t('Running Activities')!}
						nav_type="view-all"
						children={<RunningActivities runningActivities={runningActivities!} loading={runningActivitiesLoading} refetch={refetchRunning} />}
					/>
				</div>
			</div>
		</PageContainer>
	);
};

export default HomePage;
